import { useCallback, useMemo } from 'react'

import { Box, Button, Flex, Modal, Text, Title, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { RoleAttributesApiDTO } from '../../../codegen/generated/Api.schemas'
import { useGetApiRoles } from '../../../codegen/generated/roles'
import { LXTable, LXTableColumn } from '../../../components/LXTable/LXTable'
import { UsersHelper } from '../../../helpers/Users.helper'
import { useUserStore } from '../../../stores/useUserStore'
import { CreateRole } from './CreateRole'

type LXRolesItemTableType = Pick<RoleAttributesApiDTO, 'id' | 'name'> & {
  permissions: JSX.Element
}

export const RolesList = () => {
  const t = useTranslate()
  const navigate = useNavigate()
  const userStore = useUserStore()

  const [isModalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)

  const rolesQuery = useGetApiRoles()

  const columns: LXTableColumn<LXRolesItemTableType>[] = useMemo(
    () => [
      { key: 'name', value: t('_generics.name') },
      { key: 'permissions', value: t('pages.users.permissions'), type: 'component' },
    ],
    [t],
  )

  const roles = useMemo(
    () =>
      (rolesQuery.data?.body ?? []).map(r => ({
        ...r,
        id: r.id,
        permissions: UsersHelper.getPermissionsList(r.permissions, t),
      })),
    [rolesQuery.data?.body, t],
  )

  const onCreateRole = useCallback(
    (roleId: RoleAttributesApiDTO['id']) => {
      closeModal()
      navigate(`/users-roles/roles/${roleId}`)
    },
    [closeModal, navigate],
  )

  return (
    <Box py="sm">
      <Flex justify="flex-end">
        <Tooltip
          label={`⚠ ${t('pages.users.lackingPermission')} → ${t(
            'pages.users.permissionsList.roles.edit',
          )}`}
          hidden={userStore.isAuthorised('roles.edit')}
        >
          <Button
            type="button"
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={openModal}
            disabled={!userStore.isAuthorised('roles.edit')}
          >
            <Text size="xs" tt="uppercase">
              {t('pages.users.createRole')}
            </Text>
          </Button>
        </Tooltip>
      </Flex>
      <LXTable<LXRolesItemTableType>
        items={roles}
        columns={columns}
        onRowClick={r => navigate(`/users-roles/roles/${r.id}`)}
      />
      <Modal
        title={<Title size="h3">{t('pages.users.createRole')}</Title>}
        opened={isModalOpened}
        onClose={closeModal}
        size="80%"
      >
        <CreateRole onCreate={onCreateRole} />
      </Modal>
    </Box>
  )
}
